import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  newsService,
  NewsItem,
  getLocalizedString,
} from "../../services/firebaseService";
import DetailLayout from "../DetailLayout/DetailLayout";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  useEffect(() => {
    if (!id) return;

    const loadNewsItem = async () => {
      try {
        setLoading(true);
        const newsData = await newsService.getNewsItemById(id);

        console.log(newsData);
        if (newsData) {
          setArticle(newsData);
          setError(null);
        } else {
          setError("News article not found");
        }
      } catch (err) {
        console.error("Error loading news article:", err);
        setError("Failed to load news article");
      } finally {
        setLoading(false);
      }
    };

    loadNewsItem();
  }, [id]);

  const handleRetry = () => {
    if (!id) return;
    setError(null);
    setLoading(true);
    const loadNewsItem = async () => {
      try {
        const newsData = await newsService.getNewsItemById(id);
        if (newsData) {
          setArticle(newsData);
        } else {
          setError("News article not found");
        }
      } catch (err) {
        setError("Failed to load news article");
      } finally {
        setLoading(false);
      }
    };
    loadNewsItem();
  };

  return (
    <DetailLayout
      loading={loading}
      error={error}
      notFoundTitle={t("news.notFound.title")}
      backPath="/news"
      backLabel={t("common.backToNews")}
      onRetry={handleRetry}
    >
      {article &&
        (() => {
          const localizedTitle = getLocalizedString(article.title, currentLang);
          // const localizedExcerpt = getLocalizedString(
          //   article.excerpt,
          //   currentLang
          // );
          const localizedContent = getLocalizedString(
            article.content,
            currentLang
          );

          return (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 mb-12 items-start md:gap-8">
                <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-[var(--background-secondary)] group md:aspect-[16/9]">
                  <img
                    src={article.image}
                    alt={localizedTitle}
                    className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/images/placeholder.png";
                    }}
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <h1 className="text-[2.5rem] font-extrabold text-[var(--text-primary)] leading-tight m-0 md:text-[2rem]">
                    {localizedTitle}
                  </h1>
                  {/* <h2 className="text-xl text-[var(--text-secondary)] leading-normal m-0 md:text-lg">{localizedExcerpt}</h2> */}
                  <div className="flex flex-wrap gap-4 items-center mt-4 md:flex-col md:items-start md:gap-3">
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--background-secondary)] rounded-full text-sm font-medium text-primary">
                      By {article.author}
                    </span>
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--background-secondary)] rounded-full text-sm font-medium text-[var(--text-secondary)]">
                      {article.createdAt?.toDate().toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--gradient-primary)] dark:text-white  rounded-full text-sm font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid gap-8">
                <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-xl p-8 shadow-[0_2px_4px_var(--shadow-color)] md:p-6">
                  <div
                    className="text-[var(--text-secondary)] leading-relaxed [&_h1]:text-[var(--text-primary)] [&_h1]:mt-8 [&_h1]:mb-4 [&_h2]:text-[var(--text-primary)] [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:text-[var(--text-primary)] [&_h3]:mt-8 [&_h3]:mb-4 [&_h4]:text-[var(--text-primary)] [&_h4]:mt-8 [&_h4]:mb-4 [&_h5]:text-[var(--text-primary)] [&_h5]:mt-8 [&_h5]:mb-4 [&_h6]:text-[var(--text-primary)] [&_h6]:mt-8 [&_h6]:mb-4 [&_h1:first-child]:mt-0 [&_h2:first-child]:mt-0 [&_h3:first-child]:mt-0 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:pl-8 [&_ol]:mb-4 [&_ol]:pl-8 [&_li]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:my-6 [&_blockquote]:italic [&_blockquote]:text-[var(--text-secondary)] [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4"
                    dangerouslySetInnerHTML={{ __html: localizedContent }}
                  />
                </div>
              </div>
            </>
          );
        })()}
    </DetailLayout>
  );
}
